// Admin — Driver edit (Wave E).

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useDriverDetail, useUpdateDriver } from './hooks/useDriversAdmin';
import type { UpdateDriverInput } from '../../../types/api';
import styles from './DriverFormPage.module.css';

export function DriverEditPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detail = useDriverDetail(id);
  const update = useUpdateDriver(id ?? '');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!detail.data) return;
    setName(detail.data.name);
    setPhone(detail.data.phone);
    setEmail(detail.data.email ?? '');
    setAddress(detail.data.address ?? '');
    setPostcode(detail.data.postcode ?? '');
    setVehicleRegistration(detail.data.vehicle_registration ?? '');
  }, [detail.data]);

  if (detail.isLoading || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit Driver" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const original = detail.data;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch: UpdateDriverInput = {};
    if (name !== original.name) patch.name = name.trim();
    if (phone !== original.phone) patch.phone = phone.trim();
    if (email !== (original.email ?? '')) patch.email = email || null;
    if (address !== (original.address ?? '')) patch.address = address || null;
    if (postcode !== (original.postcode ?? '')) patch.postcode = postcode || null;
    if (vehicleRegistration !== (original.vehicle_registration ?? '')) {
      patch.vehicle_registration = vehicleRegistration.trim() || null;
    }
    if (newPassword) patch.new_password = newPassword;
    if (Object.keys(patch).length === 0) {
      navigate(`/admin/drivers/${encodeURIComponent(original.uuid)}`);
      return;
    }
    update.mutate(patch, {
      onSuccess: () => {
        navigate(`/admin/drivers/${encodeURIComponent(original.uuid)}`);
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit ${original.name}`}
        subtitle="Update driver details or reset password"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() =>
              navigate(`/admin/drivers/${encodeURIComponent(original.uuid)}`)
            }
          >
            Back
          </Button>
        }
      />

      <Card title="Driver Details">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Phone Number"
              variant="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Email Address (optional)"
              variant="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Vehicle registration"
              value={vehicleRegistration}
              onChange={(e) => setVehicleRegistration(e.target.value)}
              maxLength={10}
            />
          </div>

          <Textarea
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
          />

          <Input
            label="New Password (optional)"
            variant="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helper="Leave blank to keep the existing password. Min 8 characters."
            autoComplete="new-password"
          />

          <div className={styles.footer}>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate(`/admin/drivers/${encodeURIComponent(original.uuid)}`)
              }
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" icon={Save} loading={update.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
